// =============================================================================
// pwaIconGenerator — composite tenant logo onto a PWA-safe variant
//
// Why this exists:
//   Chrome's PWA install checklist silently rejects icons that have any
//   of: transparent backgrounds, dimensions smaller than 192×192, or
//   declared MIME type that doesn't match what the server returns. Most
//   custom tenant uploads fail at least one of these — and Chrome
//   doesn't tell you, it just suppresses `beforeinstallprompt`.
//
//   The Plane logo library sidesteps this by shipping pre-baked `-pwa.svg`
//   variants with opaque white rounded-square backgrounds at 512×512.
//   This helper does the same composition for ARBITRARY tenant uploads,
//   so an operator who uploads a custom transparent SVG still ends up
//   with an installable PWA.
//
// The output:
//   • 512 × 512 PNG (Chrome's preferred install dialog size)
//   • Opaque white rounded-square background, rx 112 (~22% — matches iOS
//     squircle proportions and the Plane logo template)
//   • Source logo centered with ~12% padding on each side (38 px safe
//     zone) — keeps the actual mark inside the visible area on Android's
//     maskable shapes (circle / squircle / squircle-with-corner-radius)
//
// Where it runs:
//   Client-side in the Platform Branding UI (browser Canvas API). No
//   server-side dependency added. Errors fall through to the original
//   upload — worst case the tenant just has the same install issue they
//   already had.
// =============================================================================

const PWA_CANVAS_SIZE   = 512;
const PWA_CORNER_RADIUS = 112;  // ~22% — iOS squircle, matches plane logos
const PWA_PADDING_PCT   = 0.125; // 12.5% safe zone on each side

/**
 * Take a user-uploaded image File and produce a PWA-safe PNG Blob:
 * 512 × 512, opaque white rounded-square background, source image
 * centered inside a 12.5% safe-zone padding.
 *
 * Throws if the file can't be decoded as an image (e.g. corrupt SVG,
 * unsupported format). Caller should treat the throw as "skip the PWA
 * variant" — the main upload has already succeeded by then, so the
 * tenant logo still lands; they just don't get the install-friendly
 * companion.
 */
export async function generatePwaVariant(file: File): Promise<Blob> {
  // FileReader → data URL → HTMLImageElement is the most reliable
  // decoder across SVG, PNG, JPG, WebP, GIF in browsers. Canvas's
  // drawImage handles all of them once we have an HTMLImageElement.
  const dataUrl = await fileToDataURL(file);
  const img     = await loadImage(dataUrl);

  // Some SVGs don't declare width/height attributes; naturalWidth
  // comes back as 0 in that case. Fall back to a reasonable default
  // so we still produce something Chrome can show.
  const srcW = img.naturalWidth  || PWA_CANVAS_SIZE;
  const srcH = img.naturalHeight || PWA_CANVAS_SIZE;

  const canvas = document.createElement('canvas');
  canvas.width  = PWA_CANVAS_SIZE;
  canvas.height = PWA_CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');

  // White rounded-square background. roundRect() is widely supported
  // now (Chrome 99+, Safari 16+, Firefox 113+) but we fall back to a
  // manual path for older browsers just in case.
  ctx.fillStyle = '#ffffff';
  drawRoundedRect(ctx, 0, 0, PWA_CANVAS_SIZE, PWA_CANVAS_SIZE, PWA_CORNER_RADIUS);
  ctx.fill();

  // Composite the source image centered, preserving aspect ratio.
  // The padding is on all four sides so the mark sits comfortably
  // inside Android's various maskable shapes without getting clipped.
  const targetSize = PWA_CANVAS_SIZE * (1 - PWA_PADDING_PCT * 2);
  const scale      = Math.min(targetSize / srcW, targetSize / srcH);
  const drawW      = srcW * scale;
  const drawH      = srcH * scale;
  const x          = (PWA_CANVAS_SIZE - drawW) / 2;
  const y          = (PWA_CANVAS_SIZE - drawH) / 2;
  ctx.drawImage(img, x, y, drawW, drawH);

  // PNG is the only safe output format here — JPEG would re-introduce
  // alpha problems and WebP isn't universally accepted as a PWA icon
  // type.
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null.')),
      'image/png',
    );
  });
}

// ── Helpers ────────────────────────────────────────────────────────

function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error('FileReader failed.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed (corrupt or unsupported format).'));
    img.src = src;
  });
}

/** Manual rounded-rect path. CanvasRenderingContext2D.roundRect()
 *  exists in modern browsers but isn't typed by default in the older
 *  TS lib.dom we use. Hand-rolling keeps us off the lib bump and
 *  works everywhere. */
function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Given a tenant-branding storage path like
 * `<tenant_id>/logo-1234567890.png`, return the matching PWA variant
 * path the regex in `preferPwaVariant()` will look for:
 * `<tenant_id>/logo-1234567890-pwa.png`.
 *
 * Always swaps the extension to `.png` because that's what
 * generatePwaVariant() emits regardless of the source format. The
 * downstream regex on the App side must accept this transform.
 */
export function pwaVariantPath(originalPath: string): string {
  // Strip the existing extension, append -pwa.png.
  const dot = originalPath.lastIndexOf('.');
  const base = dot >= 0 ? originalPath.slice(0, dot) : originalPath;
  return `${base}-pwa.png`;
}
